import React, {lazy, Suspense, useEffect} from 'react';
import styled from 'styled-components';
import {withRouter, Switch, Route, Redirect} from 'react-router-dom';
import {compose} from 'ramda';
import Media from 'react-responsive';
import {rootStyle} from 'styles/fragments';
import {isMobileIos} from 'utils/userAgents';
import {logInfo} from 'io/errors';
import services from 'services';
import * as notificationsActions from 'modules/notifications/actions';
import {longestDur} from 'constants/notifications';
// always include modules that are used everywhere
import Notifications from './Notifications';
import Confirmer from './Confirmer';
import SendSmsModal from './Sms/SendSmsModal';
// always include simple info pages
import NotFoundPage from 'components/generic/NotFoundPage';
import RedirectPage from 'components/views/RedirectPage';
import LoadingPage from 'components/generic/LoadingPage';
import RestrictedRoute from 'components/RestrictedRoute';
import Loading from './Loading';
import NotFound from './NotFound';
import LeaddeskClient from 'views/LeaddeskClient';
import {MenuBackdrop} from 'components/generic/Navbar';
import {SIP_PROVIDER, SIP_MEDIA_QUERY_MOBILE} from 'constants/caller';
import SipCallerClient from 'views/SipCaller/navbar/CallerClient';
import OverlayDock from 'components/OverlayDock';
import {useAuth} from 'hooks/useAuth';
import {useSelector} from 'react-redux';
import {user as userSelector} from 'modules/common/selectors';
import {userUseNewDashboard, canAccessCanaryFeatures} from 'utils/perms';
import {applyColorScheme, initializeColorScheme} from 'utils/colorScheme';

// always include core modules
import Launcher from './Launcher'; // TODO: add a ticket for launcher route removal when finished with the new dashboard
import Login from './Login';
import Dashboard from './Dashboard';

const lazyView = importFunc =>
	lazy(() =>
		importFunc().catch(e => {
			// logInfo considers errors without a description unexpected, so provide some
			e.description = '';
			logInfo(e);
			services.get('store').dispatch(
				notificationsActions.warning({
					id: 'view-load-err',
					duration: longestDur,
					message: services
						.get('intl')
						.formatMessage({id: 'Content loading failed. Try refreshing the page'}),
				}),
			);
			// React.lazy expects the imported component to be in the JS Module format, so mimick that with a dummy object
			return {default: Loading};
		}),
	);

const Notices = lazyView(() => import('./Notices'));
const ReportsApp = lazyView(() => import('./ReportsApp'));
const CallsApp = lazyView(() => import('./CallsApp'));
const SalesApp = lazyView(() => import('./SalesApp'));
const UsersApp = lazyView(() => import('./UsersApp'));
const Profile = lazyView(() => import('./Profile'));
const TeamCalendarApp = lazyView(() => import('./TeamCalendarApp'));
const StatsApp = lazyView(() => import('./StatsApp'));
const ProjectSalesApp = lazyView(() => import('./ProjectSalesApp'));
const BuildingsApp = lazyView(() => import('./BuildingsApp'));
const D2DApp = lazyView(() => import('./D2DApp'));
const CalendarApp = lazyView(() => import('./CalendarApp'));
const OverviewApp = lazyView(() => import('./OverviewApp'));
const MapsApp = lazyView(() => import('./MapsApp'));
const LeadsApp = lazyView(() => import('./LeadsApp'));
const SalesmanApp = lazyView(() => import('./SalesmanApp'));
const ReferencesApp = lazyView(() => import('./ReferencesApp'));
const MarketingApp = lazyView(() => import('./MarketingApp'));
const Feedback = lazyView(() => import('./Feedback'));
const ControlCenterApp = lazyView(() => import('./ControlCenterApp'));
const Files = lazyView(() => import('./FilesApp'));
const Canary = lazyView(() => import('./Canary'));
const Dev = lazyView(() => import('./dev'));

const Wrapper = styled.div`
	${rootStyle};
	${props => {
		if (props.isCallerVisible) {
			return `@media all and (max-width: 799px) {
				padding-bottom: 60px;
			}`;
		}
		return '';
	}}
`;

// needed because of leaddesk talk - its iframe needs to be rendered all the time, otherwise its API script crashes, so we render it at root level. the targeted child element is our actual view - since the container's content flows horizontally, we need to set a max width to our view explicitly so that it doesn't get too wide. currently the max width doesn't take into account whether the leaddesk client is displaying or not, but they fit fine on desktop-sized screens, and leaddesk talk isn't shown in apps that are intended for mobile usage.
const ViewContainer = styled.div`
	overflow: auto;
	display: flex;
	height: 100%;

	> :not(${MenuBackdrop}):not(#ld-sidebar) {
		flex-grow: 1;
		max-width: 100%;
	}
`;

/* eslint-disable react/prop-types */
const MapsApps = props => {
	const {
		match: {path, isExact},
	} = props;
	return (
		<>
			<MapsApp {...props} onBackground={!isExact} />
			{!isExact && (
				<Switch>
					<Route path={`${path}/buildings`} component={BuildingsApp} />
					<Route exact path={`${path}/sales/:id?`} component={SalesApp} />
					<Route component={NotFoundPage} />
				</Switch>
			)}
		</>
	);
};

const App = props => {
	const mobileIosProps = isMobileIos() ? {cursor: 'pointer'} : null;

	// warmup auth
	const {user, apiToken, activeOrganization} = useAuth();
	const storeUser = useSelector(userSelector);

	const useNewDasbhoard = user ? userUseNewDashboard(user) : false;
	const enioCallerStyles = user?.defaultCall === 'enioCaller' ? true : false;
	const isCallerVisible = [SIP_PROVIDER.ENIO, SIP_PROVIDER.SOMIC].includes(
		user?.defaultCall,
	);

	const IndexComponent = useNewDasbhoard ? Dashboard : Launcher;
	const authenticated = !!apiToken;
	const pathname = props?.location?.pathname || '';
	const shouldHideOverlayDock =
		!authenticated || pathname === '/login' || pathname.startsWith('/auth');
	const shouldShowOverlayDock =
		!shouldHideOverlayDock && !!storeUser && !!user && !!activeOrganization;

	// Only apply root-level dark mode within Canary routes.
	useEffect(() => {
		const pathname = props?.location?.pathname || '';
		const isCanaryRoute = pathname.startsWith('/canary');
		if (isCanaryRoute) {
			initializeColorScheme();
		} else {
			applyColorScheme(false);
		}
	}, [props?.location?.pathname]);

	return (
		<>
			<Wrapper
				id="app"
				style={{...mobileIosProps}}
				caller={enioCallerStyles}
				isCallerVisible={isCallerVisible}
			>
				<Notifications />
				<Confirmer />
				<SendSmsModal />

				{!authenticated ? (
					<Switch>
						<Route exact path="/auth/callback/azure" component={RedirectPage} />
						<Route
							exact
							path="/auth/v2/callback/azure/:accountSlug"
							component={RedirectPage}
						/>
						<Route component={Login} />
					</Switch>
				) : !storeUser || !user || !activeOrganization ? (
					<LoadingPage />
				) : (
					<Suspense fallback={<LoadingPage />}>
						<ViewContainer id="view-container">
							<Switch>
								<Redirect exact path="/logout" to="/" />
								<Redirect from="/condo-business/:id" to="/project-sales/condo/:id" />
								<Route exact path="/login" component={Loading} />
								<Route path="/auth/callback/azure/" component={RedirectPage} />
								<Route
									path="/auth/v2/callback/azure/:accountSlug"
									component={RedirectPage}
								/>
								<Route exact path="/" render={props => <IndexComponent {...props} />} />
								<Route exact path="/new" component={Dashboard} />
								<Route path="/login-test" component={Launcher} />
								<Route path="/reports" component={ReportsApp} />
								<Route path="/calls" component={CallsApp} />
								<Route path="/users" component={UsersApp} />
								<Route exact path="/sales/:id?" component={SalesApp} />
								<Route path="/notices" component={Notices} />
								<Route path="/profile" component={Profile} />
								<Route path="/team-calendar" component={TeamCalendarApp} />
								<Route path="/calendar" component={CalendarApp} />
								<Route path="/stats" component={StatsApp} />
								<Route path="/project-sales" component={ProjectSalesApp} />
								<Route path="/buildings" component={BuildingsApp} />
								<Route path="/d2d" component={D2DApp} />
								<Route path="/overview" component={OverviewApp} />
								<Route path="/maps" component={MapsApps} />
								<Route path="/leads" component={LeadsApp} />
								<Route path="/salesman-app" component={SalesmanApp} />
								<Route path="/references" component={ReferencesApp} />
								<Route path="/marketing" component={MarketingApp} />
								<Route path="/feedback" component={Feedback} />
								<Route path="/control-center" component={ControlCenterApp} />
								<Route path="/files" component={Files} />
								<RestrictedRoute
									path="/dev"
									component={Dev}
									predicate={() => process.env.NODE_ENV === 'development'}
								/>
								<RestrictedRoute
									path="/canary"
									component={Canary}
									predicate={canAccessCanaryFeatures}
								/>
								<Route component={NotFound} />
							</Switch>
							<LeaddeskClient id="ld-sidebar" />
						</ViewContainer>
					</Suspense>
				)}

				<div id="popper" className="header-dropdown-close-immune" />
				{shouldShowOverlayDock && <OverlayDock />}
			</Wrapper>
			{isCallerVisible && (
				<Media query={SIP_MEDIA_QUERY_MOBILE}>
					<div className="flex bg-white flex-1 fixed bottom-0 left-0 right-0">
						<SipCallerClient isMobile={true} />
					</div>
				</Media>
			)}
		</>
	);
};

export default compose(withRouter)(App);
